// ===========================================================================
// Where a shared video can come from.
//
// One registry, used by everything: the server validates a pinned link against
// it, the client builds the embed from it, the control adapter is chosen by
// it, and the CSP frame-src list is generated from it. Adding a site means
// adding an entry here and, if its player can be driven, one adapter. Nothing
// else in the system should ever need to learn a provider's name.
//
// Three rules hold for every entry, and none is negotiable:
//
//   * an ID PATTERN the parsed id must match. Only the id is stored or sent to
//     a client, and it is re-checked immediately before it becomes part of a
//     URL, so nothing a user types can steer the browser somewhere else.
//   * an explicit FRAME ORIGIN. It is the postMessage target and it is what
//     the CSP allows to be framed -- a provider whose player lives on a
//     different origin from its watch pages must say so (most do).
//   * a HOST ALLOWLIST, matched exactly. Never a substring search for
//     something id-shaped, which is how a link to somewhere else gets embedded.
//
// What is shared between viewers is the POSITION, never the media. Lumi does
// not fetch, store or relay a frame of anybody's video.
//
// On what is listed here: everything is a licensed, moderated, mainstream
// service with a documented public embed. Sites that exist to restream other
// people's work are not candidates however popular they are -- embedding one
// makes Lumi the surface that distributes it, and those players carry ad
// frames nobody here has vetted.
//
// ASCII only.
// ===========================================================================

/**
 * What a framed player is allowed to do.
 *
 * allow-same-origin is required and is safe for a CROSS-origin frame: the
 * usual warning is about a same-origin frame, which could then reach its own
 * frame element and strip the sandbox. Here it grants only the origin the
 * frame would have had with no sandbox at all.
 *
 * allow-popups and allow-popups-to-escape-sandbox are deliberately NOT here.
 * They were, and they meant any of the sixteen third-party origins framed by
 * this feature -- or an advert inside one, or a compromise of one -- could
 * open an UNSANDBOXED window from inside an age-gated chat. A player needs to
 * play; it does not need to open windows.
 *
 * A provider may override this. Nothing needs to yet -- but DRM-protected
 * playback is the likely first reason to, since encrypted media in a
 * sandboxed frame is exactly the sort of thing browsers restrict, and the
 * alternative would otherwise be dropping the sandbox for everybody.
 */
export const DEFAULT_FRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-presentation';

/** The sandbox attribute for a provider's frame. */
export function videoFrameSandbox(provider) {
  const p = videoProvider(provider);
  return (p && p.sandbox) || DEFAULT_FRAME_SANDBOX;
}

/** Seconds, floored and clamped -- never NaN, never negative, never absurd. */
function secs(atMs) {
  const n = Math.floor(Number(atMs) / 1000);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, 24 * 60 * 60);
}

/** The hostname a provider must be told it is being embedded by. */
function hostOf(origin) {
  try {
    return new URL(origin).hostname;
  } catch {
    return '';
  }
}

/** Parse a user-supplied string into a URL, or null. Never throws. */
function asUrl(input) {
  if (typeof input !== 'string') return null;
  const raw = input.trim();
  if (!raw) return null;
  let url;
  try {
    url = new URL(raw.includes('://') ? raw : `https://${raw}`);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  // Credentials in a URL are only ever there to make one host look like
  // another to whoever is reading it.
  if (url.username || url.password) return null;
  return url;
}

/**
 * The providers, ranked by how much use each is likely to get.
 *
 * `control` names an adapter that will ATTEMPT to drive the player. It is not
 * a promise that it works: these protocols are semi-documented and several
 * adapters are written from a published description rather than from a
 * confirmed run. Whether a room gets shared controls is decided at runtime by
 * the player answering, so a wrong guess costs a silent handshake rather than
 * a timeline that does nothing when you drag it.
 *
 * Null means the player cannot be commanded. It does NOT mean everyone is on
 * their own: a provider whose embed URL takes a start position can still be
 * synchronised by rebuilding the frame at the room's position whenever
 * somebody moves it. `startsAt` says whether that is possible. It is coarser
 * than driving a player -- a reload, not a seek -- but it puts everybody at
 * the same moment, which is the point of watching together.
 */
const DEFS = [
  // ---- Global ------------------------------------------------------------
  {
    id: 'youtube',
    name: 'YouTube',
    rank: 1,
    kind: 'video',
    hosts: ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com', 'youtu.be', 'www.youtu.be'],
    frameOrigin: 'https://www.youtube-nocookie.com',
    startsAt: true,
    idPattern: /^[A-Za-z0-9_-]{11}$/,
    control: 'youtube',
    watchUrl: (id) => `https://www.youtube.com/watch?v=${id}`,
    parse(url) {
      if (url.hostname.toLowerCase().endsWith('youtu.be')) {
        return url.pathname.slice(1).split('/')[0] || null;
      }
      const v = url.searchParams.get('v');
      if (v) return v;
      const m = url.pathname.match(/^\/(embed|shorts|live|v)\/([^/?#]+)/);
      return m ? m[2] : null;
    },
    embed: (id, atMs, origin, o = {}) =>
      `https://www.youtube-nocookie.com/embed/${id}`
      + `?enablejsapi=1&rel=0&modestbranding=1&playsinline=1`
      + `&origin=${encodeURIComponent(origin)}&start=${secs(atMs)}`
      + `&autoplay=${o.autoplay ? 1 : 0}`,
  },
  {
    id: 'spotify',
    name: 'Spotify',
    rank: 2,
    kind: 'audio',
    hosts: ['open.spotify.com', 'play.spotify.com'],
    frameOrigin: 'https://open.spotify.com',
    // "<kind>~<id>": Spotify addresses tracks, albums, playlists and podcast
    // episodes the same way but embeds them at different paths.
    startsAt: false,
    idPattern: /^(track|album|playlist|episode|show)~[A-Za-z0-9]{16,30}$/,
    control: null,
    watchUrl: (id) => `https://open.spotify.com/${id.replace('~', '/')}`,
    parse(url) {
      const m = url.pathname.match(/^\/(?:intl-[a-z]{2}\/)?(track|album|playlist|episode|show)\/([A-Za-z0-9]+)/);
      return m ? `${m[1]}~${m[2]}` : null;
    },
    embed: (id) => `https://open.spotify.com/embed/${id.replace('~', '/')}`,
  },
  {
    id: 'twitch',
    name: 'Twitch',
    rank: 3,
    kind: 'video',
    hosts: ['twitch.tv', 'www.twitch.tv', 'm.twitch.tv'],
    frameOrigin: 'https://player.twitch.tv',
    // "v~<numeric vod>" or "c~<channel>". A live channel has no position to
    // share, which is fine -- it has no position for anyone.
    startsAt: true,
    idPattern: /^(v~[0-9]{6,15}|c~[A-Za-z0-9_]{3,25})$/,
    control: null,
    watchUrl: (id) =>
      id.startsWith('v~') ? `https://www.twitch.tv/videos/${id.slice(2)}` : `https://www.twitch.tv/${id.slice(2)}`,
    parse(url) {
      const vod = url.pathname.match(/^\/videos\/([0-9]+)/);
      if (vod) return `v~${vod[1]}`;
      const ch = url.pathname.match(/^\/([A-Za-z0-9_]{3,25})\/?$/);
      return ch ? `c~${ch[1]}` : null;
    },
    embed(id, atMs, origin, o = {}) {
      // Twitch refuses to frame unless told which page is doing it.
      const parent = `parent=${encodeURIComponent(hostOf(origin))}`;
      const auto = o.autoplay ? 'true' : 'false';
      return id.startsWith('v~')
        ? `https://player.twitch.tv/?video=${id.slice(2)}&time=${secs(atMs)}s&autoplay=${auto}&${parent}`
        : `https://player.twitch.tv/?channel=${id.slice(2)}&autoplay=${auto}&${parent}`;
    },
  },
  {
    id: 'vimeo',
    name: 'Vimeo',
    rank: 4,
    kind: 'video',
    hosts: ['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'],
    frameOrigin: 'https://player.vimeo.com',
    startsAt: true,
    idPattern: /^[0-9]{6,12}$/,
    control: 'vimeo',
    watchUrl: (id) => `https://vimeo.com/${id}`,
    parse(url) {
      const m = url.pathname.match(/(?:^\/video\/|^\/)([0-9]{6,12})/);
      return m ? m[1] : null;
    },
    // dnt=1 asks Vimeo not to track the viewer. They honour it, and it costs
    // nothing to send.
    embed: (id, atMs, origin, o = {}) =>
      `https://player.vimeo.com/video/${id}?dnt=1&autoplay=${o.autoplay ? 1 : 0}#t=${secs(atMs)}s`,
  },
  {
    id: 'soundcloud',
    name: 'SoundCloud',
    rank: 5,
    kind: 'audio',
    hosts: ['soundcloud.com', 'www.soundcloud.com', 'm.soundcloud.com'],
    frameOrigin: 'https://w.soundcloud.com',
    // SoundCloud's widget takes the track's own URL, so the id is its path.
    startsAt: false,
    idPattern: /^[a-z0-9][a-z0-9_-]{1,60}\/[a-z0-9][a-z0-9_-]{1,80}$/,
    control: 'soundcloud',
    watchUrl: (id) => `https://soundcloud.com/${id}`,
    parse(url) {
      const m = url.pathname.match(/^\/([a-z0-9][a-z0-9_-]*)\/([a-z0-9][a-z0-9_-]*)\/?$/i);
      return m ? `${m[1].toLowerCase()}/${m[2].toLowerCase()}` : null;
    },
    embed: (id) =>
      `https://w.soundcloud.com/player/?url=${encodeURIComponent(`https://soundcloud.com/${id}`)}`
      + `&auto_play=false&hide_related=true&show_comments=false&visual=true`,
  },
  {
    id: 'dailymotion',
    name: 'Dailymotion',
    rank: 6,
    kind: 'video',
    hosts: ['dailymotion.com', 'www.dailymotion.com', 'dai.ly'],
    frameOrigin: 'https://geo.dailymotion.com',
    // The player navigates its own frame between these two.
    frameOrigins: ['https://geo.dailymotion.com', 'https://www.dailymotion.com'],
    startsAt: true,
    idPattern: /^[A-Za-z0-9]{6,12}$/,
    // Measured: the player sends one message and then ignores {command:play}
    // with and without parameters, bare "play", {method:play}, notifyOnEvents
    // and seek. Talks once, obeys nothing -- like OK.ru and Niconico.
    control: null,
    watchUrl: (id) => `https://www.dailymotion.com/video/${id}`,
    parse(url) {
      if (url.hostname.toLowerCase() === 'dai.ly') return url.pathname.slice(1).split('/')[0] || null;
      const m = url.pathname.match(/^\/(?:video|embed\/video)\/([A-Za-z0-9]+)/);
      return m ? m[1] : null;
    },
    // queue-enable=false is the important one. By default this player ends on
    // an "Up next" card recommending a video nobody in the room chose, served
    // straight into a shared space Lumi is answerable for. Turning off the
    // queue, the autoplay-next and the sharing panel leaves the host's choice
    // as the only thing on screen.
    embed: (id, atMs, origin, o = {}) => `https://geo.dailymotion.com/player.html?video=${id}`
      + `&startTime=${secs(atMs)}&autoplay=${o.autoplay ? 'true' : 'false'}`
      + '&queue-enable=false&queue-autoplay-next=false&sharing-enable=false&ui-logo=false',
  },

  // ---- Regional ----------------------------------------------------------
  {
    id: 'bilibili',
    name: 'Bilibili',
    rank: 7,
    kind: 'video',
    hosts: ['bilibili.com', 'www.bilibili.com', 'm.bilibili.com', 'b23.tv'],
    frameOrigin: 'https://player.bilibili.com',
    startsAt: true,
    idPattern: /^BV[A-Za-z0-9]{10}$/,
    control: null,
    watchUrl: (id) => `https://www.bilibili.com/video/${id}`,
    parse(url) {
      const m = url.pathname.match(/\/(BV[A-Za-z0-9]{10})/);
      return m ? m[1] : null;
    },
    embed: (id, atMs, origin, o = {}) =>
      `https://player.bilibili.com/player.html?bvid=${id}&t=${secs(atMs)}&autoplay=${o.autoplay ? 1 : 0}`,
  },
  {
    id: 'vk',
    name: 'VK Video',
    rank: 8,
    kind: 'video',
    hosts: ['vk.com', 'www.vk.com', 'm.vk.com', 'vkvideo.ru', 'www.vkvideo.ru'],
    frameOrigin: 'https://vkvideo.ru',
    frameOrigins: ['https://vkvideo.ru', 'https://vk.com'],
    // VK addresses a video by owner AND id. Kept as one opaque string so the
    // rest of the system still handles a single id, split only at the embed.
    startsAt: true,
    idPattern: /^-?[0-9]{1,20}_[0-9]{1,20}$/,
    control: 'vk',
    watchUrl: (id) => `https://vkvideo.ru/video${id}`,
    parse(url) {
      const m = url.pathname.match(/^\/(?:video|clip)(-?[0-9]+_[0-9]+)/);
      if (m) return m[1];
      const z = String(url.searchParams.get('z') || '').match(/^video(-?[0-9]+_[0-9]+)/);
      return z ? z[1] : null;
    },
    embed(id, atMs, origin, o = {}) {
      const [oid, vid] = id.split('_');
      return `https://vkvideo.ru/video_ext.php?oid=${oid}&id=${vid}&hd=2&t=${secs(atMs)}s`
        + `&autoplay=${o.autoplay ? 1 : 0}`;
    },
  },
  {
    id: 'rutube',
    name: 'RuTube',
    rank: 9,
    kind: 'video',
    hosts: ['rutube.ru', 'www.rutube.ru'],
    frameOrigin: 'https://rutube.ru',
    startsAt: true,
    idPattern: /^[0-9a-f]{32}$/,
    control: 'rutube',
    watchUrl: (id) => `https://rutube.ru/video/${id}/`,
    parse(url) {
      const m = url.pathname.match(/^\/(?:video|shorts|play\/embed)\/([0-9a-fA-F]{32})/);
      return m ? m[1].toLowerCase() : null;
    },
    embed: (id, atMs) => `https://rutube.ru/play/embed/${id}/?t=${secs(atMs)}`,
  },
  {
    id: 'ok',
    name: 'OK.ru',
    rank: 10,
    kind: 'video',
    hosts: ['ok.ru', 'www.ok.ru', 'm.ok.ru', 'odnoklassniki.ru', 'www.odnoklassniki.ru'],
    frameOrigin: 'https://ok.ru',
    startsAt: false,
    idPattern: /^[0-9]{6,20}$/,
    // Measured, not assumed. The player greets us with {"event":"inited"}
    // and then ignores {command:play}, bare "play", {method:play},
    // {command:start}, {command:resume} and {event:play} alike, and never
    // reports a position. Claiming control here put a "Play for everyone"
    // button on screen that did nothing at all.
    control: null,
    watchUrl: (id) => `https://ok.ru/video/${id}`,
    parse(url) {
      const m = url.pathname.match(/^\/(?:video|videoembed)\/([0-9]{6,20})/);
      return m ? m[1] : null;
    },
    embed: (id) => `https://ok.ru/videoembed/${id}`,
  },
  {
    // Large in Japan and effectively unknown outside it, so it ranks last
    // rather than by its actual size. Rank is display order, not importance.
    id: 'niconico',
    name: 'Niconico',
    rank: 14,
    kind: 'video',
    hosts: ['nicovideo.jp', 'www.nicovideo.jp', 'sp.nicovideo.jp', 'nico.ms'],
    frameOrigin: 'https://embed.nicovideo.jp',
    startsAt: true,
    idPattern: /^[a-z]{2}[0-9]{1,12}$/,
    // Measured, not assumed, and it is a shame. With jsapi=1 the player sends
    // loadComplete with the video's details and then ignores every command:
    // {eventName:play} with and without a data field, with _frontendId, plus
    // getStatus and seek. Five shapes, no reply, no state change. Like OK.ru it
    // will talk once and obey nothing, so the room must not pretend otherwise
    // -- a Play button that cannot start anything is worse than no button.
    control: null,
    watchUrl: (id) => `https://www.nicovideo.jp/watch/${id}`,
    parse(url) {
      const m = url.pathname.match(/\/(?:watch\/)?([a-z]{2}[0-9]+)\/?$/);
      return m ? m[1] : null;
    },
    // jsapi=1 and playerId are what make the player speak postMessage at all.
    // Without them it plays perfectly and answers nothing, which looks exactly
    // like an adapter with the wrong protocol. Confirmed against sm9.
    embed: (id, atMs) =>
      `https://embed.nicovideo.jp/watch/${id}?jsapi=1&playerId=1&from=${secs(atMs)}`,
  },

  // ---- Audio, talks, archives -------------------------------------------
  {
    id: 'mixcloud',
    name: 'Mixcloud',
    rank: 11,
    kind: 'audio',
    hosts: ['mixcloud.com', 'www.mixcloud.com'],
    frameOrigin: 'https://player-widget.mixcloud.com',
    startsAt: false,
    idPattern: /^[a-z0-9][a-z0-9_-]{1,60}\/[a-z0-9][a-z0-9_-]{1,100}$/,
    control: null,
    watchUrl: (id) => `https://www.mixcloud.com/${id}/`,
    parse(url) {
      const m = url.pathname.match(/^\/([a-z0-9][a-z0-9_-]*)\/([a-z0-9][a-z0-9_-]*)\/?$/i);
      return m ? `${m[1].toLowerCase()}/${m[2].toLowerCase()}` : null;
    },
    embed: (id) =>
      `https://player-widget.mixcloud.com/widget/iframe/?feed=${encodeURIComponent(`/${id}/`)}&hide_cover=1`,
  },
  {
    id: 'ted',
    name: 'TED',
    rank: 12,
    kind: 'video',
    hosts: ['ted.com', 'www.ted.com', 'embed.ted.com'],
    frameOrigin: 'https://embed.ted.com',
    startsAt: false,
    idPattern: /^[a-z0-9][a-z0-9_]{3,80}$/,
    control: null,
    watchUrl: (id) => `https://www.ted.com/talks/${id}`,
    parse(url) {
      const m = url.pathname.match(/\/talks\/([a-z0-9_]+)/i);
      return m ? m[1].toLowerCase() : null;
    },
    embed: (id) => `https://embed.ted.com/talks/${id}`,
  },
  {
    id: 'archive',
    name: 'Internet Archive',
    rank: 13,
    kind: 'video',
    hosts: ['archive.org', 'www.archive.org'],
    frameOrigin: 'https://archive.org',
    startsAt: true,
    idPattern: /^[A-Za-z0-9][A-Za-z0-9._-]{1,90}$/,
    control: null,
    watchUrl: (id) => `https://archive.org/details/${id}`,
    parse(url) {
      const m = url.pathname.match(/^\/(?:details|embed)\/([A-Za-z0-9][A-Za-z0-9._-]*)/);
      return m ? m[1] : null;
    },
    embed: (id, atMs, origin, o = {}) =>
      `https://archive.org/embed/${id}?start=${secs(atMs)}&autoplay=${o.autoplay ? 1 : 0}`,
  },
];

export const VIDEO_PROVIDERS = Object.freeze(DEFS.map(Object.freeze));

/** By id, e.g. 'youtube'. Undefined for anything not registered. */
export function videoProvider(id) {
  return DEFS.find((p) => p.id === id);
}

/** Every origin that may be framed. The CSP frame-src list is built from this. */
export function videoFrameOrigins() {
  return [...new Set(DEFS.flatMap((p) => p.frameOrigins || [p.frameOrigin]))].sort();
}

/** Every host a pasted link may come from -- for the "what can I paste?" hint. */
export function videoProviderNames() {
  return DEFS.slice().sort((a, b) => a.rank - b.rank).map((p) => p.name);
}

/**
 * The provider and id behind a link, or null.
 *
 * @param {string} input a URL, or the canonical "provider:id" form
 * @returns {{ provider: string, videoId: string }|null}
 */
export function parseVideoUrl(input) {
  if (typeof input !== 'string') return null;
  const raw = input.trim();
  if (!raw) return null;

  // The canonical stored form, and the only form a client may send back. A
  // real URL cannot be mistaken for it: "https" is not a registered provider.
  const tagged = raw.match(/^([a-z0-9]+):(.+)$/i);
  if (tagged) {
    const p = videoProvider(tagged[1].toLowerCase());
    if (p && p.idPattern.test(tagged[2])) return { provider: p.id, videoId: tagged[2] };
  }

  const url = asUrl(raw);
  if (!url) return null;
  const provider = DEFS.find((p) => p.hosts.includes(url.hostname.toLowerCase()));
  if (!provider) return null;

  let id;
  try {
    id = provider.parse(url);
  } catch {
    return null; // a provider's parser must never take the process with it
  }
  if (typeof id !== 'string' || !provider.idPattern.test(id)) return null;
  return { provider: provider.id, videoId: id };
}

/**
 * The URL to frame, or null if the pair is not one we recognise.
 *
 * The id is re-checked here even though it was checked when parsed. This is
 * the last point before a string becomes a URL the browser loads, and it is
 * reached with values that have round-tripped through the server and back.
 */
export function videoEmbedUrl({ provider, videoId, atMs = 0, origin = '', autoplay = false }) {
  const p = videoProvider(provider);
  if (!p || typeof videoId !== 'string' || !p.idPattern.test(videoId)) return null;
  try {
    // autoplay matters for the providers the room follows by rebuilding
    // their frame: without it a rebuild lands everybody at the right
    // moment and then just sits there, which is half a feature.
    return p.embed(videoId, atMs, origin, { autoplay: !!autoplay });
  } catch {
    return null;
  }
}

/** Where to send somebody who would rather watch it on the site itself. */
export function videoWatchUrl({ provider, videoId }) {
  const p = videoProvider(provider);
  if (!p || typeof videoId !== 'string' || !p.idPattern.test(videoId)) return null;
  return p.watchUrl(videoId);
}

/** Can this provider's player be COMMANDED (play/pause/seek)? */
export function videoSupportsSync(provider) {
  const p = videoProvider(provider);
  return !!(p && p.control);
}

/**
 * Can the room put everyone at the same moment, one way or another?
 *
 * True when the player takes commands, and ALSO when it merely accepts a
 * start position in its URL -- because a frame can be rebuilt there. The
 * second is coarser and costs a reload, but it is the difference between
 * watching together and watching the same thing alone.
 */
export function videoCanFollow(provider) {
  const p = videoProvider(provider);
  return !!(p && (p.control || p.startsAt));
}

export default {
  DEFAULT_FRAME_SANDBOX,
  videoFrameSandbox,
  VIDEO_PROVIDERS,
  videoProvider,
  videoFrameOrigins,
  videoProviderNames,
  parseVideoUrl,
  videoEmbedUrl,
  videoWatchUrl,
  videoSupportsSync,
  videoCanFollow,
};
